// Last known render positions of the entities the VFX layer follows (deployed units, strikes in flight). A killed
// unit or a finished strike is usually despawned from the mirror by the time its event plays, so its effects use
// the position cached here. Bounded: when full, an entry the mirror no longer shows is recycled first, then the
// oldest.

import { NO_SIM_ID, type SimId } from '@taxwar/net'
import type { LatLonOut, MatchView } from '../match/contracts'

/** live = the mirror knows it this frame; lost = seen before, gone now; unknown = never seen. */
export type PositionState = 'live' | 'lost' | 'unknown'

export class LastKnownPositions {
  private readonly ids: Float64Array
  private readonly lats: Float64Array
  private readonly lons: Float64Array
  private readonly seen: Uint8Array
  private readonly live: Uint8Array
  private readonly slots = new Map<number, number>()
  private cursor = 0
  private readonly scratch: LatLonOut = { lat: 0, lon: 0 }

  constructor(
    private readonly match: MatchView,
    private readonly capacity: number,
  ) {
    this.ids = new Float64Array(capacity)
    this.lats = new Float64Array(capacity)
    this.lons = new Float64Array(capacity)
    this.seen = new Uint8Array(capacity)
    this.live = new Uint8Array(capacity)
  }

  /** Starts following `id` (no-op if already followed). Event-time only: the slot search is O(capacity). */
  track(id: SimId): void {
    if (id === NO_SIM_ID || this.slots.has(id)) return
    const slot = this.claimSlot()
    this.cursor = (slot + 1) % this.capacity
    const evicted = this.ids[slot] as number
    if (evicted !== NO_SIM_ID) this.slots.delete(evicted)
    this.ids[slot] = id
    this.seen[slot] = 0
    this.live[slot] = 0
    this.slots.set(id, slot)
    this.refreshSlot(slot)
  }

  forget(id: SimId): void {
    const slot = this.slots.get(id)
    if (slot === undefined) return
    this.slots.delete(id)
    this.ids[slot] = NO_SIM_ID
  }

  /** Once per frame: re-reads every followed entity's interpolated position. */
  refresh(): void {
    for (let slot = 0; slot < this.capacity; slot++) {
      if (this.ids[slot] !== NO_SIM_ID) this.refreshSlot(slot)
    }
  }

  /** Current position if the mirror knows it, else the last cached one; false when neither exists. */
  locate(id: SimId, out: LatLonOut): boolean {
    const slot = this.slots.get(id)
    if (slot !== undefined) {
      this.refreshSlot(slot)
      if (this.seen[slot] === 0) return false
      out.lat = this.lats[slot] as number
      out.lon = this.lons[slot] as number
      return true
    }
    return this.match.entityPosition(id, out)
  }

  /** State of a followed entity as of the last refresh; its cached position goes to `out` when seen. */
  stateOf(id: SimId, out: LatLonOut): PositionState {
    const slot = this.slots.get(id)
    if (slot === undefined || this.seen[slot] === 0) return 'unknown'
    out.lat = this.lats[slot] as number
    out.lon = this.lons[slot] as number
    return this.live[slot] === 1 ? 'live' : 'lost'
  }

  /**
   * A free slot; else one whose entity was seen but has left the mirror (died or walked out of sight unannounced);
   * else the oldest (the cursor). Never-seen entries are pending, not stale: a strike's event can precede its view.
   */
  private claimSlot(): number {
    let stale = -1
    for (let n = 0; n < this.capacity; n++) {
      const slot = (this.cursor + n) % this.capacity
      if (this.ids[slot] === NO_SIM_ID) return slot
      if (stale < 0 && this.seen[slot] === 1 && this.live[slot] === 0) stale = slot
    }
    return stale < 0 ? this.cursor : stale
  }

  private refreshSlot(slot: number): void {
    const known = this.match.entityPosition(this.ids[slot] as SimId, this.scratch)
    this.live[slot] = known ? 1 : 0
    if (!known) return
    this.seen[slot] = 1
    this.lats[slot] = this.scratch.lat
    this.lons[slot] = this.scratch.lon
  }
}
